import { useState, useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
  InteractionRequiredAuthError,
  InteractionStatus,
} from "@azure/msal-browser";
import { loginWithMicrosoft, User, logout } from "../services/api";
import { useBranding } from "../hooks/useBranding";
import styles from "./LoginPage.module.css";
import { brandingMap } from "../config/brandingMap";
import apiClient from "../services/apiClient";
import { toast } from "react-toastify";

interface Props {
  onLogin: (user: User) => void;
}

export const LoginPage: React.FC<Props> = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [showMSLogin, setShowMSLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { instance, inProgress } = useMsal();
  const location = useLocation();
  const navigate = useNavigate();
  const fromPath = (location.state as any)?.from?.pathname || "/";

  // Email validation function
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Logout when accessing login page
  useEffect(() => {
    const performLogout = async () => {
      // Only logout if there's an actual session
      // Don't logout if we're being redirected here due to auth failure
      const isRedirectedFromAuth = location.state?.from;
      
      if (!isRedirectedFromAuth) {
        try {
          await logout();
          // Clear local user state via onLogin callback
          onLogin(null as any);
        } catch (error) {
          console.error("Error during automatic logout:", error);
        }
      }
    };

    performLogout();
  }, []);

  const handleLogin = async () => {
    try {
      setMicrosoftLoading(true);
      setLoading(true);
      setError(null);

      // Define login request with correct scopes
      const loginRequest = {
        scopes: ["user.read"],
        prompt: "select_account",
        // Add redirectUri explicitly to ensure it doesn't clear the hash
        redirectUri: window.location.origin + window.location.pathname,
      };

      console.log("Starting MSAL login process...");

      // Save current state before login
      const currentState = {
        fromPath,
        redirectAfterLogin: sessionStorage.getItem("redirectAfterLogin"),
      };
      sessionStorage.setItem("msalAuthState", JSON.stringify(currentState));

      // Use loginPopup with proper error handling
      const response = await instance.loginPopup(loginRequest);
      console.log("MSAL login successful, token acquired");

      // Verify we have a valid access token
      if (!response || !response.accessToken) {
        throw new Error("No access token received from authentication");
      }

      console.log("Sending token to backend...");
      const backend = await loginWithMicrosoft(response.accessToken);
      console.log("Backend login response:", backend);

      if (backend.authenticated) {
        console.log(
          "Login successful, navigating to:",
          backend.redirectUrl || fromPath
        );

        // Check if user data is available in the response
        if (backend.user) {
          // Create a complete user object by combining user data and roles
          const userWithRoles = {
            ...backend.user,
            roles: backend.roles || [],
          };
          onLogin(userWithRoles);
        }

        // Retrieve the saved state (in case it was lost during auth)
        let savedState;
        try {
          const stateData = sessionStorage.getItem("msalAuthState");
          if (stateData) {
            savedState = JSON.parse(stateData);
          }
        } catch (e) {
          console.warn("Could not retrieve saved state:", e);
        }

        // Priorität:
        // 1. Die redirectUrl vom Backend, wenn vorhanden
        // 2. Der gespeicherte Wert aus sessionStorage
        // 3. Der Wert aus dem gespeicherten State
        // 4. Der Ursprungspfad aus dem Router-State
        // 5. Der Standard-Pfad '/'
        const redirectTarget =
          backend.redirectUrl ||
          sessionStorage.getItem("redirectAfterLogin") ||
          savedState?.redirectAfterLogin ||
          savedState?.fromPath ||
          fromPath ||
          "/";

        console.log("Final redirect target:", redirectTarget);

        // Clear the saved state after use
        sessionStorage.removeItem("msalAuthState");

        // Echtes Hard-Redirect durchführen für vollständigen Seitenneuladen
        window.location.href = redirectTarget;
      } else {
        setError("Backend authentication failed");
      }
    } catch (err: any) {
      console.error("Login error:", err);
      
      // Handle specific MSAL errors
      if (err instanceof InteractionRequiredAuthError) {
        setError("Authentication requires additional interaction");
      } else if (
        err.errorCode === "empty_hash_error" ||
        err.errorMessage?.includes("hash value cannot be processed")
      ) {
        setError("Authentication process was interrupted. Please try again.");

        // Force re-initialization of MSAL
        try {
          await instance.handleRedirectPromise();
        } catch (e) {
          console.warn("Error handling redirect:", e);
        }
      } else if (err.response) {
        // Handle HTTP errors from backend
        const status = err.response.status;
        const data = err.response.data;
        
        if (status === 403) {
          // Spezielle Behandlung für inaktive Accounts
          setError(data?.detail || "Access denied. Your account may be inactive. Please contact support.");
        } else if (data?.detail) {
          setError(data.detail);
        } else {
          setError("Authentication failed. Please try again.");
        }
      } else {
        setError(err instanceof Error ? err.message : "Authentication failed");
      }
    } finally {
      setLoading(false);
      setMicrosoftLoading(false);
    }
  };

  const handleMagicLink = async () => {
    setMagicLinkLoading(true);
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const redirect_url = window.location.origin; // z. B. https://localhost:3000
      const forward_url = sessionStorage.getItem("redirectAfterLogin") || "/";

      await apiClient.post("/api/v2/web/auth/magic-link/request", {
        email,
        URL: redirect_url,
        forward_url,
      });

      // Success: Zeige Bestätigung direkt auf der Seite
      setSuccessMessage("Magic link sent! Please check your email.");
    } catch (err: any) {
      console.error("Magic link error:", err);
      
      // Granulare Fehlerbehandlung
      let errorMessage = 'Failed to send magic link';
      let showToast = false; // Nur bei Verbindungsproblemen Toast anzeigen
      
      if (err.response) {
        // Server hat mit einem Fehlercode geantwortet
        const status = err.response.status;
        const data = err.response.data;
        
        if (status === 400) {
          errorMessage = data?.detail || 'Invalid email address or request.';
        } else if (status === 403) {
          // Spezielle Behandlung für inaktive Accounts oder fehlende Verifizierung
          errorMessage = data?.detail || 'Access denied. Please contact support.';
        } else if (status === 404) {
          errorMessage = 'Email address not found. Please check your email or contact support.';
        } else if (status === 429) {
          errorMessage = 'Too many requests. Please wait a moment and try again.';
        } else if (status === 500) {
          errorMessage = data?.detail || 'Server error. Please try again later.';
        } else if (data?.detail) {
          errorMessage = data.detail;
        } else {
          errorMessage = `Error (${status}). Please try again.`;
        }
      } else if (err.request) {
        // Request wurde gesendet, aber keine Antwort erhalten - Verbindungsproblem
        errorMessage = 'No response from server. Please check your internet connection.';
        showToast = true; // Zeige Toast bei Verbindungsproblemen
      } else if (err.message) {
        // Fehler beim Setup des Requests
        if (err.message.includes('Network Error')) {
          errorMessage = 'Network error. Please check your internet connection.';
          showToast = true; // Zeige Toast bei Netzwerkproblemen
        } else if (err.message.includes('timeout')) {
          errorMessage = 'Request timeout. Please try again.';
          showToast = true; // Zeige Toast bei Timeout
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      
      // Nur bei Verbindungsproblemen Toast anzeigen
      if (showToast) {
        toast.error(`❌ ${errorMessage}`, {
          position: "top-center",
          autoClose: 5000,
        });
      }
    } finally {
      setLoading(false);
      setMagicLinkLoading(false);
    }
  };

  return (
    <div className={styles.loginWrapper}>
      <div className={styles.loginContainer}>
        <h1>Login</h1>
        <div className={styles.loginForm} style={{ width: "80%" }}>
          <input
            type="email"
            id="email"
            className={styles.loginEmail}
            value={email}
            onChange={(e) => {
              const v = e.target.value;
              setEmail(v);
              setShowMSLogin(v.endsWith("@ypog.law"));
            }}
            disabled={loading}
            placeholder="Enter your email address"
            autoComplete="email"
          />

          {error && (
            <div
              className={styles.errorMessage}
            >
              {error}
            </div>
          )}

          {successMessage && (
            <div
              className={styles.successMessage}
            >
              ✅ {successMessage}
            </div>
          )}

          <div className={styles.loginFormButtons}>
          <button
            onClick={handleMagicLink}
            disabled={loading || !email || !isValidEmail(email)}
            className="btn btn-primary"
          >
            {magicLinkLoading && (
              <span
                id="magic-link-spinner"
                className="spinner-border spinner-border-sm me-2"
              ></span>
            )}
            {magicLinkLoading ? "Sending..." : "Send Log-In Link"}
          </button>
            
          {showMSLogin && (
            <button
              onClick={handleLogin}
              disabled={loading || inProgress !== InteractionStatus.None}
              className="btn btn-primary"
            >
              {microsoftLoading && (
                <span
                  id="microsoft-login-spinner"
                  className="spinner-border spinner-border-sm me-2"
                ></span>
              )}
              {!microsoftLoading && (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-microsoft me-2" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                  <path d="M7.462 0H0v7.19h7.462zM16 0H8.538v7.19H16zM7.462 8.211H0V16h7.462zm8.538 0H8.538V16H16z"/>
                </svg>
              )}
              {microsoftLoading ? "Logging in..." : "Login with Microsoft SSO"}
            </button>
          )}
</div>


          {useBranding.name === "MatchMaker" ? (
            <div className={styles.footer}>
              Not a member?{" "}
              <br />
              <a href="mailto:events@ypog.law?subject=MatchMaker%20%7C%20Support%20Request%20">
                Reach out to the YPOG Events-Team!
              </a>
            </div>
          ) : (
            <div className={styles.footer}>
              Not a member?{" "}
              <br />
              <a href="mailto:events@ypog.law?subject=Managing%20Partners'%20Speed%20Dating%20%7C%20Support%20Request">
                Reach out to the YPOG Events-Team!
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
